import { JSX } from "react";
import { Col, Divider, Row, Tag } from "antd";
import { PercentageOutlined } from "@ant-design/icons";
import Text from "../../components/Text/Text";
import color from "../../resource/color";
import { ShopTierCompanyConfig } from "../ShopTier/config";
import { ConfigTierBenefit } from "../ShopTier/mockData";
import TierCardPreview from "./TierCardPreview";
import { EditableTierBenefit } from "./types";
import { toRebatePercent } from "./rebatePercent";

interface Props {
  config: ShopTierCompanyConfig;
  tiers: EditableTierBenefit[];
  cardImageOverrides: Record<string, string>;
  cardBackgroundOverrides?: Record<string, string>;
  colorOverrides: Record<string, string>;
  originalTiers?: ConfigTierBenefit[];
  changedTierKeys?: string[];
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <Text fontSize={12} color="Text3">{children}</Text>
    </div>
  );
}

function TierReviewList({
  config,
  tiers,
  cardImageOverrides,
  cardBackgroundOverrides = {},
  colorOverrides,
  originalTiers = [],
  changedTierKeys = [],
}: Props): JSX.Element {
  return (
    <>
      {config.tiers.map((t) => {
        const tierData = tiers.find((x) => x.tierKey === t.key);
        const benefits = tierData?.benefits || [];
        const percent = toRebatePercent(tierData?.percent);
        const tierColor = tierData?.color || colorOverrides[t.key] || t.color;
        const isCustomColor = Boolean(colorOverrides[t.key]);
        const backgroundOverride = cardBackgroundOverrides[t.key];
        const isEntryTier = t.key === "Follow" || t.key === "Basic";
        const showAutoDemoteToggle = config.company !== "ICPL";

        const originalTier = originalTiers.find((o) => o.tierKey === t.key);
        const currentTitles = benefits.map((b) => b.title.trim()).filter(Boolean);
        const originalTitles = originalTier?.benefits || [];
        const addedTitles = currentTitles.filter((title) => !originalTitles.includes(title));
        const removedTitles = originalTitles.filter((title) => !currentTitles.includes(title));
        const isEdited = Boolean(originalTier) && changedTierKeys.includes(t.key);
        const changeSummary = isEdited
          ? [
              ...addedTitles.map((title) => `เพิ่ม "${title}"`),
              ...removedTitles.map((title) => `เอาออก "${title}"`),
            ]
          : [];

        return (
          <div
            key={t.key}
            style={{
              padding: isEdited ? 24 : 0,
              background: isEdited ? color.background1 : "transparent",
              borderRadius: 24,
              border: isEdited ? `1px solid ${color.border}` : "none",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <Tag style={{ borderRadius: 16, border: "none", background: tierColor, margin: 0 }}>
                <Text fontSize={14} fontWeight={700} color="white">{t.label}</Text>
              </Tag>
              <Text fontSize={13} color="Text3">เกณฑ์ {t.subLabel}</Text>
              <Text fontSize={13} color="Text3">·</Text>
              <Text fontSize={13} color="Text3">{benefits.length} สิทธิประโยชน์</Text>
              {isEdited && (
                <Tag style={{ borderRadius: 16, margin: 0, backgroundColor: color.background3, border: "none" }}>
                  <Text fontSize={13} fontWeight={700} color="primary">แก้ไข</Text>
                </Tag>
              )}
            </div>

            <Row gutter={24}>
              <Col flex="370px">
                <FieldLabel>รูปการ์ดระดับ</FieldLabel>
                <TierCardPreview tier={t} imageOverride={cardImageOverrides[t.key]} width="100%" />

                <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
                  <div>
                    <FieldLabel>พื้นหลังของระดับ</FieldLabel>
                    <div
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: 8,
                        border: `1px solid ${color.border}`,
                        overflow: "hidden",
                        background: color.background1,
                      }}
                    >
                      {backgroundOverride && (
                        <img src={backgroundOverride} alt="พื้นหลังของระดับ" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      )}
                    </div>
                  </div>
                  <div>
                    <FieldLabel>สีของระดับ</FieldLabel>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 20, height: 20, borderRadius: "50%", background: tierColor, border: `1px solid ${color.border}`, flexShrink: 0 }} />
                      <div>
                        <Text fontWeight={700} fontSize={13} color="Text2">{tierColor.toUpperCase()}</Text>
                        <div>
                          <Text fontSize={12} color="Text3">{isCustomColor ? "กำหนดเอง" : "สีจากทางบริษัท"}</Text>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>

              <Col flex="auto" style={{ minWidth: 0 }}>
                <FieldLabel>สิทธิประโยชน์หลัก</FieldLabel>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    background: color.background1,
                    borderRadius: 8,
                    padding: "10px 12px",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: "#FFF",
                        border: `1px solid ${color.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <PercentageOutlined style={{ color: color.background4 }} />
                    </div>
                    <div>
                      <Text fontWeight={700} fontSize={14} color="Text2">{tierData?.tierTitle || "-"}</Text>
                      {tierData?.titleDescription && (
                        <div>
                          <Text fontSize={13} color="Text3">{tierData.titleDescription}</Text>
                        </div>
                      )}
                    </div>
                  </div>
                  <Text fontSize={18} fontWeight={700} color="secondary3">{percent}%</Text>
                </div>

                <FieldLabel>รายการสิทธิประโยชน์</FieldLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {benefits.length > 0 ? (
                    benefits.map((b, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: color.background1,
                          borderRadius: 8,
                          padding: "10px 12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                        }}
                      >
                        <div>
                          <Text fontWeight={700} fontSize={14} color="Text2">{b.title || "-"}</Text>
                          {b.description && (
                            <div>
                              <Text fontSize={13} color="Text3">{b.description}</Text>
                            </div>
                          )}
                        </div>
                        {b.iconUrl ? (
                          <img src={b.iconUrl} alt="" style={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0 }} />
                        ) : (
                          <span style={{ width: 28, height: 6, borderRadius: 4, background: color.background2, flexShrink: 0 }} />
                        )}
                      </div>
                    ))
                  ) : (
                    <Text fontSize={13} color="Text3">ยังไม่มีสิทธิประโยชน์ในระดับนี้</Text>
                  )}
                </div>

                {changeSummary.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Text fontSize={13} color="primary">{changeSummary.join(" · ")}</Text>
                  </div>
                )}

                {!isEntryTier && tierData && (
                  <div style={{ marginTop: 16 }}>
                    <FieldLabel>เงื่อนไขการพิจารณาระดับ</FieldLabel>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <Text fontSize={13} color="Text2">
                        เข้าคิวพิจารณาเลื่อนขั้นที่ {tierData.queueThresholdPercent}% ของเกณฑ์
                      </Text>
                      {showAutoDemoteToggle && (
                        <Text fontSize={13} color="Text2">
                          ห้ามระบบลดระดับนี้อัตโนมัติ: {tierData.preventAutoDemote ? "เปิด" : "ปิด"}
                        </Text>
                      )}
                    </div>
                  </div>
                )}
              </Col>
            </Row>

            <Divider />
          </div>
        );
      })}
    </>
  );
}

export default TierReviewList;
